import React, { useState } from 'react';
import { Card, Col, Row, Spin, Typography, Tooltip, Switch, Space } from 'antd';
import { RiseOutlined, FallOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { getAppStats } from '../../services/appEventsService';

const { Text } = Typography;

const formatInt = (n) =>
  typeof n === 'number' && !Number.isNaN(n) ? Math.round(n).toLocaleString() : '—';

const formatMoney = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return '—';
  const s = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `$${s}`;
};

const formatGrowInt = (grow) => {
  if (typeof grow !== 'number' || Number.isNaN(grow)) return '—';
  const sign = grow > 0 ? '+' : grow < 0 ? '-' : '';
  const abs = Math.abs(Math.round(grow)).toLocaleString();
  return `${sign}${abs}`;
};

const formatGrowMoney = (grow) => {
  if (typeof grow !== 'number' || Number.isNaN(grow)) return '—';
  const sign = grow > 0 ? '+' : grow < 0 ? '-' : '';
  const abs = Math.abs(grow).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sign}$${abs}`;
};

/**
 * Dashboard-style stat card: large end-of-period value, pill trend badge, baseline "from …"
 */
const StatCard = ({ title, titleTooltip, metric, mode = 'int' }) => {
  const { atStart, atEnd, grow, changePercent } = metric || {};

  const primary = mode === 'money' ? formatMoney(atEnd) : formatInt(atEnd);

  const baselineLabel = mode === 'money' ? formatMoney(atStart) : formatInt(atStart);

  const pctPart =
    changePercent != null && typeof changePercent === 'number' && !Number.isNaN(changePercent)
      ? ` (${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%)`
      : '';

  const growPart = mode === 'money' ? formatGrowMoney(grow) : formatGrowInt(grow);
  const trendPositive = typeof grow === 'number' && grow > 0;
  const trendNegative = typeof grow === 'number' && grow < 0;

  const badgeBg = trendPositive ? '#f6ffed' : trendNegative ? '#fff2f0' : '#fafafa';
  const badgeColor = trendPositive ? '#52c41a' : trendNegative ? '#ff4d4f' : '#8c8c8c';
  const BadgeIcon = trendNegative ? FallOutlined : RiseOutlined;

  const titleNode = titleTooltip ? (
    <Tooltip title={titleTooltip} overlayInnerStyle={{ maxWidth: 380 }}>
      <span style={{ cursor: 'help', borderBottom: '1px dashed rgba(0,0,0,0.22)' }}>{title}</span>
    </Tooltip>
  ) : (
    title
  );

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        padding: '16px 18px 14px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ fontSize: 13, color: '#8c8c8c', fontWeight: 500, marginBottom: 10 }}>{titleNode}</div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          color: 'rgba(0,0,0,0.88)',
          lineHeight: 1.2,
          marginBottom: 14,
          letterSpacing: '-0.02em',
        }}
      >
        {primary}
      </div>
      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 999,
            background: badgeBg,
            color: badgeColor,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <BadgeIcon style={{ fontSize: 12 }} />
          <span>
            {growPart}
            {pctPart}
          </span>
        </span>
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          from {baselineLabel}
        </Text>
      </div>
    </div>
  );
};

const GROSS_MRR_HELP =
  'Sum of active paid plan amounts (plan amount > 0) across merchants with an active install at the end of the selected range. Compared to the sum at the start of the range (00:00 first day, timezone +07:00).';

const NET_MRR_HELP =
  'Discount-adjusted subscription value (per plan rules), then minus Shopify Partner revenue share (15% on the portion of your selected date range on or after Jan 16, 2026), then minus operating fee (3.9%). Same comparison window as Gross MRR.';

const PLAN_CHART_HEIGHT = 420;

const formatGrowthLabel = (row) => {
  const atStart = Number(row?.atStart) || 0;
  const atEnd = Number(row?.atEnd) || 0;
  const pct = Number(row?.growthPercent);
  if (atStart === 0 && atEnd === 0) return '0%';
  if (atStart === 0 && atEnd > 0) return '+100.0%';
  if (Number.isNaN(pct)) return '—';
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
};

/** Badge colors on chart (positive / negative / flat / empty). */
const growthBadgeTheme = (row) => {
  const atStart = Number(row?.atStart) || 0;
  const atEnd = Number(row?.atEnd) || 0;
  const pct = Number(row?.growthPercent);
  if (atStart === 0 && atEnd === 0) {
    return { bg: '#fafafa', fg: '#8c8c8c', border: '#d9d9d9' };
  }
  if (atStart === 0 && atEnd > 0) {
    return { bg: '#f6ffed', fg: '#389e0d', border: '#b7eb8f' };
  }
  if (Number.isNaN(pct)) {
    return { bg: '#fafafa', fg: '#8c8c8c', border: '#d9d9d9' };
  }
  if (pct > 0) return { bg: '#f6ffed', fg: '#389e0d', border: '#b7eb8f' };
  if (pct < 0) return { bg: '#fff2f0', fg: '#cf1322', border: '#ffccc7' };
  return { bg: '#fafafa', fg: '#595959', border: '#d9d9d9' };
};

const PlanDistributionChart = ({ rows }) => {
  if (!rows?.length) {
    return (
      <Text type="secondary" style={{ display: 'block', padding: '24px 0' }}>
        No plan periods found for this app with the current filters.
      </Text>
    );
  }

  const categories = rows.map((r) => r.planName);

  const peaks = rows.map((r) => Math.max(Number(r.atStart) || 0, Number(r.atEnd) || 0));
  const dataMax = Math.max(1, ...peaks);
  const yaxisMax = Math.ceil(dataMax * 1.42);

  const growthAnnotations = {
    points: rows.map((row) => {
      const peak = Math.max(Number(row.atStart) || 0, Number(row.atEnd) || 0);
      const gap = Math.max(0.55, dataMax * 0.045);
      let y = peak === 0 ? Math.min(yaxisMax * 0.14, Math.max(1.2, dataMax * 0.08)) : peak + gap;
      y = Math.min(y, yaxisMax * 0.96);
      const theme = growthBadgeTheme(row);
      return {
        x: row.planName,
        y,
        marker: { size: 0 },
        label: {
          text: formatGrowthLabel(row),
          offsetY: -2,
          borderColor: theme.border,
          borderWidth: 1,
          borderRadius: 10,
          style: {
            background: theme.bg,
            color: theme.fg,
            fontSize: '11px',
            fontWeight: 700,
            padding: {
              left: 9,
              right: 9,
              top: 4,
              bottom: 4,
            },
          },
        },
      };
    }),
  };

  const options = {
    chart: {
      type: 'bar',
      height: PLAN_CHART_HEIGHT,
      toolbar: { show: true },
      zoom: { enabled: false },
      padding: { top: 4 },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 2,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: { show: true, width: 2, colors: ['transparent'] },
    xaxis: {
      categories,
      labels: {
        style: { fontSize: '11px' },
        maxHeight: 100,
      },
    },
    yaxis: {
      title: { text: 'Stores' },
      min: 0,
      max: yaxisMax,
      decimalsInFloat: 0,
      labels: {
        formatter: (val) => `${Math.round(val)}`,
      },
    },
    annotations: growthAnnotations,
    legend: { position: 'top' },
    colors: ['#91caff', '#1677ff'],
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => `${Math.round(val)} stores`,
      },
    },
  };

  const series = [
    { name: 'At period start', data: rows.map((r) => r.atStart) },
    { name: 'At period end', data: rows.map((r) => r.atEnd) },
  ];

  return <Chart options={options} series={series} type="bar" height={PLAN_CHART_HEIGHT} />;
};

const AppStatsSection = ({ appId, dateRange, shopifyPlan, country }) => {
  const [excludeFreePlans, setExcludeFreePlans] = useState(false);

  const [start, end] = dateRange || [];
  const dateStart = start && dayjs.isDayjs(start) ? start.format('YYYY-MM-DD') : null;
  const dateEnd = end && dayjs.isDayjs(end) ? end.format('YYYY-MM-DD') : null;
  const enabled = Boolean(appId && dateStart && dateEnd);

  const { data, isLoading, isError, error } = useQuery(
    ['app-stats', appId, dateStart, dateEnd, shopifyPlan, country, excludeFreePlans],
    () =>
      getAppStats({
        appId,
        dateStart,
        dateEnd,
        ...(shopifyPlan ? { shopifyPlan } : {}),
        ...(country ? { country } : {}),
        excludeFreePlans,
      }),
    { enabled, staleTime: 60 * 1000 }
  );

  if (!enabled) {
    return (
      <Card style={{ marginTop: 16 }}>
        <Text type="secondary">Select a date range to load App Stats.</Text>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (isError) {
    return (
      <Card style={{ marginTop: 16 }}>
        <Text type="danger">{error?.response?.data?.message || 'Failed to load App Stats.'}</Text>
      </Card>
    );
  }

  const revPct = data?.revenueShare?.fractionOfRangeSubjectToRevShare;
  const revPctLabel =
    typeof revPct === 'number' && !Number.isNaN(revPct) ? `${(revPct * 100).toFixed(2)}%` : '—';

  const milestoneLabel = data?.revenueShareMilestone
    ? dayjs(data.revenueShareMilestone).format('YYYY-MM-DD')
    : '2026-01-16';

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 16, maxWidth: 960 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Metrics compare shop state at the start of the range (00:00 on the first day) vs the end (23:59:59 on the
          last day), {data?.range?.timezone || '+07:00'}.
        </Text>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          MRR calculation method
        </Text>
        <ol
          type="1"
          style={{
            margin: 0,
            paddingLeft: 22,
            color: 'rgba(0,0,0,0.45)',
            fontSize: 14,
            lineHeight: 1.65,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <li>
            MRR here is derived by looking at which active paid licenses exist at each snapshot (start and end of the
            range). It answers: if those licenses were invoiced and paid in full right at that moment, how much
            subscription revenue would that represent?
          </li>
          <li>
            In practice, stores do not always pay immediately—billing may land in a later month, or the store may
            close before charge—so these numbers can differ from actual Shopify Partner payouts.
          </li>
          <li>
            <strong>Net MRR</strong> applies plan discounts, then revenue share (
            {revPctLabel} of the selected period falls on or after {milestoneLabel}) at 15%, then a 3.9% operating fee.
          </li>
        </ol>
      </div>
      <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 12 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} xl={6}>
            <StatCard title="Total active merchant" metric={data?.totalMerchants} mode="int" />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <StatCard title="Total Paid Merchants" metric={data?.totalPaidMerchants} mode="int" />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <StatCard
              title="Total Gross MRR"
              titleTooltip={GROSS_MRR_HELP}
              metric={data?.totalGrossMrr}
              mode="money"
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <StatCard title="Total Net MRR" titleTooltip={NET_MRR_HELP} metric={data?.totalNetMrr} mode="money" />
          </Col>
        </Row>
      </div>

      <Card
        style={{ marginTop: 16 }}
        title="Plan distribution"
        extra={
          <Space size="middle" wrap>
            <Tooltip
              title="When on, only plan names that appear with plan amount greater than 0 on at least one stored period are shown. Stores whose covering plan at a snapshot has amount 0 are not counted."
              overlayInnerStyle={{ maxWidth: 340 }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Switch checked={excludeFreePlans} onChange={setExcludeFreePlans} />
                <Text type="secondary">Exclude amount $0</Text>
              </span>
            </Tooltip>
            <Tooltip
              title="Among shops with an active install at each snapshot, counts how many have each app plan (plan period covering that instant). Plan names come from planPeriods on records (paid-only names when Exclude amount $0 is on). Growth in labels: start count 0 → end positive is +100%."
              overlayInnerStyle={{ maxWidth: 360 }}
            >
              <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', cursor: 'help' }} />
            </Tooltip>
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Stores per plan at <strong>period start</strong> vs <strong>period end</strong>. Colored badges above each group
          show growth (green positive, red negative, gray flat or empty). Store counts appear in the tooltip.
        </Text>
        <PlanDistributionChart rows={data?.planDistribution || []} />
      </Card>
    </div>
  );
};

export default AppStatsSection;
